import { createRng, hashSeed } from "./rng";

export const BOUNTY_INTERVAL_MIN_S = 36;
export const BOUNTY_INTERVAL_MAX_S = 50;
export const BOUNTY_DURATION_S = 20;
export const BOUNTY_SCORE = 280;
export const BOUNTY_XP = 16;

export type BountyCandidate = {
  id: number;
  elite: boolean;
  isAce: boolean;
  isArenaGuardian: boolean;
  hull: number;
  x: number;
  y: number;
};

export type BountyState = {
  targetId: number | null;
  remainingS: number;
  nextAt: number;
  bonusScore: number;
};

export type BountySnapshot = {
  targetId: number;
  remainingS: number;
  bonusScore: number;
};

export function createBountyState(simTime: number, seed: number): BountyState {
  return {
    targetId: null,
    remainingS: 0,
    nextAt: simTime + scheduleBountyDelay(simTime, seed, 1),
    bonusScore: BOUNTY_SCORE,
  };
}

export function scheduleBountyDelay(simTime: number, seed: number, tick: number): number {
  const rng = createRng(hashSeed(seed, tick * 17 + Math.floor(simTime * 10)));
  return BOUNTY_INTERVAL_MIN_S + rng() * (BOUNTY_INTERVAL_MAX_S - BOUNTY_INTERVAL_MIN_S);
}

export function pickBountyTarget(
  enemies: readonly BountyCandidate[],
  player: { x: number; y: number },
  seed: number,
  tick: number,
): BountyCandidate | null {
  const live = enemies.filter((e) => e.hull > 0);
  if (live.length === 0) return null;
  const scored = live.map((enemy) => {
    const dist = Math.hypot(enemy.x - player.x, enemy.y - player.y);
    let score = 80 - Math.min(dist, 80);
    if (enemy.isArenaGuardian) score += 80;
    else if (enemy.isAce) score += 70;
    else if (enemy.elite) score += 40;
    return { enemy, score };
  });
  scored.sort((a, b) => b.score - a.score);
  const top = scored.slice(0, Math.min(3, scored.length));
  const rng = createRng(hashSeed(seed, tick * 31 + live.length * 7));
  return top[Math.floor(rng() * top.length)]!.enemy;
}

export function tickBounty(
  state: BountyState,
  simTime: number,
  dt: number,
  enemies: readonly BountyCandidate[],
  player: { x: number; y: number },
  seed: number,
  tick: number,
): "mark" | "expire" | null {
  if (state.targetId != null) {
    const target = enemies.find((e) => e.id === state.targetId && e.hull > 0);
    state.remainingS -= dt;
    if (!target || state.remainingS <= 0) {
      state.targetId = null;
      state.remainingS = 0;
      state.nextAt = simTime + scheduleBountyDelay(simTime, seed, tick);
      return "expire";
    }
    return null;
  }

  if (simTime < state.nextAt) return null;
  const picked = pickBountyTarget(enemies, player, seed, tick);
  if (!picked) {
    state.nextAt = simTime + 6;
    return null;
  }
  state.targetId = picked.id;
  state.remainingS = BOUNTY_DURATION_S;
  return "mark";
}

export function clearBounty(state: BountyState, simTime: number, seed: number, tick: number): void {
  state.targetId = null;
  state.remainingS = 0;
  state.nextAt = simTime + scheduleBountyDelay(simTime, seed, tick);
}

export function bountySnapshot(state: BountyState): BountySnapshot | null {
  if (state.targetId == null) return null;
  return {
    targetId: state.targetId,
    remainingS: state.remainingS,
    bonusScore: state.bonusScore,
  };
}
